// =====================================================
// COMPONENTE DE GESTIÓN DE CONTRASEÑAS MEJORADO
// =====================================================

import React, { useState, useEffect } from 'react';
import { Key, Save, AlertCircle, CheckCircle, Eye, EyeOff, Download, Trash2, Zap, FileText } from 'lucide-react';
import { simpleHash } from '../utils/auth';
import {
  changeClientPassword,
  changeAdminPassword,
  getPendingChangesSummary,
  generateUpdateScript,
  clearAllPasswordOverrides,
  getDisplayPassword,
  generateManualInstructions,
  generateUpdatedDatabaseJson
} from '../utils/passwordManager';
import database from '../database.json';

const COLORS_CORP = {
  darkBg: '#0F172A',
  button: '#A6C9EC',
  buttonText: '#1e293b'
};

const PasswordManager = () => {
  const [selectedClient, setSelectedClient] = useState('');
  const [currentPasswordDisplay, setCurrentPasswordDisplay] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [pendingChanges, setPendingChanges] = useState({ total: 0, clients: 0, admin: false });

  // Lista de clientes + admin
  const clientOptions = [
    { id: 'ADMIN', nombre: 'Administrador' },
    ...database.clientes
  ];

  // Cargar resumen de cambios pendientes
  useEffect(() => {
    const summary = getPendingChangesSummary();
    setPendingChanges(summary);
  }, [selectedClient]);

  // Cargar contraseña actual cuando se selecciona cliente
  useEffect(() => {
    if (selectedClient) {
      const displayPassword = getDisplayPassword(selectedClient);
      setCurrentPasswordDisplay(displayPassword);
    } else {
      setCurrentPasswordDisplay('');
    }
  }, [selectedClient]);

  const clearForm = () => {
    setSelectedClient('');
    setCurrentPasswordDisplay('');
    setNewPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setShowCurrentPassword(false);
    setMessage({ type: '', text: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    // Validaciones
    if (!selectedClient) {
      setMessage({ type: 'error', text: 'Selecciona un usuario' });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'La contraseña debe tener al menos 6 caracteres' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Las contraseñas no coinciden' });
      return;
    }

    setIsUpdating(true);

    try {
      // Generar hash de la nueva contraseña
      const passwordHash = await simpleHash(newPassword);
      
      // Actualizar según tipo de usuario
      if (selectedClient === 'ADMIN') {
        changeAdminPassword(passwordHash);
        setMessage({ 
          type: 'success', 
          text: 'Contraseña de administrador actualizada correctamente' 
        });
      } else {
        changeClientPassword(selectedClient, passwordHash);
        const clientName = clientOptions.find(c => c.id === selectedClient)?.nombre;
        setMessage({ 
          type: 'success', 
          text: `Contraseña actualizada para ${clientName}` 
        });
      }
      
      // Actualizar contador de cambios pendientes
      const summary = getPendingChangesSummary();
      setPendingChanges(summary);
      
      // Actualizar la contraseña mostrada si es el mismo usuario
      if (selectedClient) {
        const updatedPassword = getDisplayPassword(selectedClient);
        setCurrentPasswordDisplay(updatedPassword);
      }
      
      // Limpiar formulario después de 3 segundos
      setTimeout(() => {
        clearForm();
      }, 3000);

    } catch (error) {
      setMessage({ type: 'error', text: 'Error al actualizar la contraseña' });
      console.error('Password update error:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDownloadScript = () => {
    const script = generateUpdateScript();
    if (!script) {
      setMessage({ type: 'error', text: 'No hay cambios pendientes' });
      return;
    }

    // Crear y descargar archivo
    const blob = new Blob([script], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'update-passwords.sh';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setMessage({ type: 'success', text: 'Script descargado correctamente' });
  };

  const handleClearChanges = () => {
    if (window.confirm('¿Estás seguro de eliminar todos los cambios pendientes?')) {
      clearAllPasswordOverrides();
      setPendingChanges({ total: 0, clients: 0, admin: false });
      setMessage({ type: 'success', text: 'Cambios pendientes eliminados' });
      clearForm();
    }
  };

  const handleDownloadInstructions = () => {
    const instructions = generateManualInstructions();
    if (!instructions) {
      setMessage({ type: 'error', text: 'No hay cambios pendientes' });
      return;
    }

    // Crear y descargar archivo
    const blob = new Blob([instructions], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'aplicar-cambios-produccion.md';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setMessage({ type: 'success', text: 'Instrucciones descargadas correctamente' });
  };

  const handleDownloadNewDatabase = () => {
    const newDatabase = generateUpdatedDatabaseJson();
    if (!newDatabase) {
      setMessage({ type: 'error', text: 'No hay cambios pendientes' });
      return;
    }

    // Crear y descargar archivo
    const blob = new Blob([newDatabase], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'database-updated.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setMessage({ type: 'success', text: 'Nuevo database.json descargado. Reemplaza el archivo original y haz commit.' });
  };

  return (
    <div className="space-y-6">
      {/* Resumen de cambios pendientes */}
      {pendingChanges.total > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600" />
              <div>
                <h4 className="font-medium text-blue-900">
                  {pendingChanges.total} cambio{pendingChanges.total > 1 ? 's' : ''} pendiente{pendingChanges.total > 1 ? 's' : ''}
                </h4>
                <p className="text-sm text-blue-700">
                  {pendingChanges.clients} cliente{pendingChanges.clients !== 1 ? 's' : ''}
                  {pendingChanges.admin && pendingChanges.clients > 0 ? ' + admin' : ''}
                  {pendingChanges.admin && pendingChanges.clients === 0 ? ' admin' : ''}
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={handleDownloadNewDatabase}
                style={{ backgroundColor: '#059669', color: 'white' }}
                className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium hover:opacity-90"
                title="Descarga database.json actualizado para reemplazar el original"
              >
                <Zap className="w-3 h-3" />
                Aplicar a Producción
              </button>
              <button
                onClick={handleDownloadInstructions}
                style={{ backgroundColor: COLORS_CORP.button, color: COLORS_CORP.buttonText }}
                className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium hover:opacity-90"
                title="Descarga instrucciones paso a paso"
              >
                <FileText className="w-3 h-3" />
                Instrucciones
              </button>
              <button
                onClick={handleDownloadScript}
                className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium text-slate-600 hover:bg-slate-100"
                title="Script automático (avanzado)"
              >
                <Download className="w-3 h-3" />
                Script
              </button>
              <button
                onClick={handleClearChanges}
                className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-3 h-3" />
                Limpiar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Formulario principal */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <Key className="w-5 h-5" style={{ color: COLORS_CORP.button }} />
          <h3 className="font-semibold text-slate-900 text-lg">Gestión de Contraseñas</h3>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Selector de Usuario */}
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">
              Usuario
            </label>
            <select 
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
              disabled={isUpdating}
            >
              <option value="">Selecciona un usuario...</option>
              {clientOptions.map(client => (
                <option key={client.id} value={client.id}>
                  {client.id === 'ADMIN' ? '👑 ADMIN - Administrador' : `${client.id} - ${client.nombre}`}
                </option>
              ))}
            </select>
          </div>

          {/* Contraseña Actual */}
          {selectedClient && (
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">
                Contraseña Actual
              </label>
              <div className="relative">
                <input 
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPasswordDisplay}
                  readOnly
                  className="w-full px-3 py-2 pr-10 text-sm border border-slate-200 rounded-lg bg-slate-50 text-slate-600"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}

          {/* Nueva Contraseña */}
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">
              Nueva Contraseña
            </label>
            <div className="relative">
              <input 
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                className="w-full px-3 py-2 pr-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
                disabled={isUpdating}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                disabled={isUpdating}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Confirmar Contraseña */}
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">
              Confirmar Contraseña
            </label>
            <input 
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repite la contraseña"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
              disabled={isUpdating}
            />
          </div>

          {/* Mensaje de Estado */}
          {message.text && (
            <div className={`flex items-center gap-2 p-3 rounded-lg ${
              message.type === 'success' 
                ? 'bg-green-50 text-green-700 border border-green-200' 
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {message.type === 'success' 
                ? <CheckCircle className="w-4 h-4" />
                : <AlertCircle className="w-4 h-4" />
              }
              <span className="text-sm">{message.text}</span>
            </div>
          )}

          {/* Botones */}
          <div className="flex gap-2 pt-2">
            <button 
              type="submit"
              disabled={isUpdating || !selectedClient || !newPassword || !confirmPassword}
              style={{ 
                backgroundColor: COLORS_CORP.button, 
                color: COLORS_CORP.buttonText,
                opacity: (isUpdating || !selectedClient || !newPassword || !confirmPassword) ? 0.6 : 1
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition disabled:hover:opacity-60"
            >
              <Save className="w-4 h-4" />
              {isUpdating ? 'Actualizando...' : 'Cambiar Contraseña'}
            </button>
            
            <button 
              type="button"
              onClick={clearForm}
              disabled={isUpdating}
              className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition disabled:opacity-60"
            >
              Limpiar
            </button>
          </div>
        </form>

        {/* Información */}
        <div className="mt-6 p-4 bg-slate-50 rounded-lg">
          <h4 className="font-medium text-slate-700 text-sm mb-2">🔄 Flujo de trabajo:</h4>
          <ul className="text-xs text-slate-600 space-y-1">
            <li>• <strong>Pruebas:</strong> Los cambios son inmediatos - puedes usar la nueva contraseña al instante</li>
            <li>• <strong>Temporal:</strong> Se guardan en el navegador para pruebas</li>
            <li>• <strong>Producción:</strong> Usa "Aplicar a Producción" para generar archivos actualizados</li>
            <li>• <strong>Deploy:</strong> Reemplaza archivos + commit + push → Funciona para todos los usuarios</li>
          </ul>
          
          {pendingChanges.total > 0 && (
            <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
              <strong>⚠️ Recordatorio:</strong> Los cambios actuales solo funcionan en TU navegador. 
              Para que funcionen para todos los usuarios, descarga y aplica los archivos actualizados.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordManager;